import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import { t } from 'i18next';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';

import AutocompleteAllUserAvailableCompaniesTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteAllUserAvailableCompaniesTypeahead';
import TextInput from '~components/FormFields/v2/TextInput';
import { Department } from '~hooks/useDepartment/models';
import { useStores } from '~store';

import {
  DepartmentDto,
  DepartmentDtoAsyncError,
  departmentSchema,
  setDepartmentSchemaDefaultValues,
} from './departmentSchema';

export type DepartmentFormHandler = {
  onSubmit: () => Promise<DepartmentDto | undefined>;
};

interface DepartmentFormProps {
  asyncErrors?: DepartmentDtoAsyncError[];
  department?: Department;
}

const DepartmentForm = forwardRef<DepartmentFormHandler, DepartmentFormProps>(
  function DepartmentForm({ asyncErrors, department }, ref) {
    const { userStore } = useStores();

    const isEditing = Boolean(department);

    const { handleSubmit, control, setError, clearErrors } = useForm<DepartmentDto>({
      resolver: yupResolver(departmentSchema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues: setDepartmentSchemaDefaultValues(userStore, department),
    });

    useImperativeHandle(
      ref,
      () => ({
        onSubmit() {
          return new Promise((resolve) => {
            handleSubmit(
              (data) => {
                resolve(data);
              },
              () => {
                resolve(undefined);
              },
            )();
          });
        },
      }),
      [handleSubmit],
    );

    useEffect(() => {
      if (asyncErrors) {
        for (const error of asyncErrors) {
          setError(error.field as keyof DepartmentDto, { message: error.message });
        }
      }

      return () => {
        clearErrors();
      };
    }, [asyncErrors]);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}>
        <Box>
          <AutocompleteAllUserAvailableCompaniesTypeahead
            control={control}
            disableClearable
            isDisabled={isEditing}
            isRequired
            label={t('form_fields.company')}
            name="company"
          />
        </Box>

        <Box>
          <TextInput
            control={control}
            isRequired
            label={t('form_fields.name')}
            name="name"
          />
        </Box>
      </Box>
    );
  },
);

export default DepartmentForm;
